import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Layers, MessageCircle, Send } from "lucide-react";
import { useNavigate } from "react-router";

const actions = [
  {
    id: "categories",
    label: "Explore Categories",
    sub: "See all six verticals",
    icon: Layers,
    href: "/#categories",
    isRoute: true,
    style: "gold",
  },
  {
    id: "ask",
    label: "Ask a Question",
    sub: "Get answers from our team",
    icon: Send,
    href: "/contact",
    isRoute: true,
    style: "dark",
  },
  {
    id: "expert",
    label: "Talk to an Expert",
    sub: "Connect with a verified professional",
    icon: MessageCircle,
    href: "/contact",
    isRoute: true,
    style: "outline",
  },
];

/**
 * 3D-tilt card shell — reacts to mouse position.
 */
function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [5, -5]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), { stiffness: 300, damping: 30 });

  const handleMouse = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformPerspective: 900 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const FinalCTA = () => {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);

  const handleAction = (action) => {
    if (action.id === "categories") {
      const el = document.getElementById("categories");
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    navigate(action.href);
  };

  return (
    <section className="relative overflow-hidden bg-gray-950 px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
      {/* layered background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,210,33,0.18),transparent_55%)]" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FFD221]/60 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.8) 1px,transparent 1px)",
          backgroundSize: "44px 44px",
        }}
        aria-hidden
      />
      {/* floating gold orbs */}
      <div className="pointer-events-none absolute -left-24 top-1/3 h-64 w-64 rounded-full bg-[#FFD221]/10 blur-[90px]" aria-hidden />
      <div className="pointer-events-none absolute -right-16 bottom-1/4 h-56 w-56 rounded-full bg-[#FFD221]/08 blur-[80px]" aria-hidden />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Headline */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-5 text-xs font-black uppercase tracking-[0.28em] text-[#FFD221]/70">
            Final decision
          </p>
          <h2 className="text-[clamp(2.5rem,6vw+0.5rem,4.5rem)] font-black leading-[1.02] tracking-tight text-white">
            Ready to Make the{" "}
            <span
              className="relative inline-block bg-gradient-to-r from-[#FFD221] via-[#ffe566] to-[#c9a000] bg-clip-text text-transparent"
            >
              Right Decision?
              {/* underline swoosh */}
              <motion.span
                className="absolute -bottom-2 left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-[#FFD221] via-[#ffe566] to-transparent"
                initial={{ scaleX: 0, originX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
            </span>
          </h2>
          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/60">
            Start with a question, explore a category, or speak directly with a verified expert.
            The right path is one click away.
          </p>
        </motion.div>

        {/* Action cards */}
        <div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-3 sm:gap-5">
          {actions.map((action, index) => {
            const Icon = action.icon;
            const isGold = action.style === "gold";
            const isDark = action.style === "dark";
            const isHovered = hoveredId === action.id;

            return (
              <TiltCard key={action.id}>
                <motion.div
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-20px" }}
                  transition={{ duration: 0.45, delay: index * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  onHoverStart={() => setHoveredId(action.id)}
                  onHoverEnd={() => setHoveredId(null)}
                  whileHover={{ scale: 1.025 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleAction(action)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleAction(action)}
                  aria-label={action.label}
                  className={`group relative flex cursor-pointer flex-col gap-5 overflow-hidden rounded-[1.75rem] p-6 transition-shadow sm:p-7 ${
                    isGold
                      ? "bg-[#FFD221] text-black shadow-[0_20px_60px_-24px_rgba(255,210,33,0.8)] hover:shadow-[0_28px_80px_-20px_rgba(255,210,33,0.9)]"
                      : isDark
                      ? "border border-white/10 bg-white/[0.06] text-white shadow-[0_20px_60px_-32px_rgba(0,0,0,0.5)] backdrop-blur-sm hover:bg-white/[0.09]"
                      : "border border-[#FFD221]/35 bg-transparent text-white shadow-none hover:bg-[#FFD221]/06"
                  }`}
                >
                  {/* glow blob */}
                  <div
                    className={`pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl transition-opacity ${
                      isGold
                        ? "bg-white/25 opacity-60 group-hover:opacity-90"
                        : "bg-[#FFD221]/20 opacity-40 group-hover:opacity-70"
                    }`}
                    aria-hidden
                  />

                  {/* icon */}
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl transition-transform group-hover:scale-110 ${
                      isGold
                        ? "bg-black/10 text-black"
                        : isDark
                        ? "bg-[#FFD221]/20 text-[#FFD221]"
                        : "border border-[#FFD221]/30 bg-[#FFD221]/10 text-[#FFD221]"
                    }`}
                  >
                    <Icon size={26} strokeWidth={2.2} />
                  </div>

                  {/* text */}
                  <div className="flex-1">
                    <p
                      className={`text-lg font-black leading-tight sm:text-xl ${
                        isGold ? "text-black" : "text-white"
                      }`}
                    >
                      {action.label}
                    </p>
                    <p
                      className={`mt-2 text-sm leading-relaxed ${
                        isGold ? "text-black/65" : "text-white/55"
                      }`}
                    >
                      {action.sub}
                    </p>
                  </div>

                  {/* arrow */}
                  <div className="flex items-center justify-end">
                    <motion.div
                      animate={{ x: isHovered ? 4 : 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                      className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                        isGold
                          ? "bg-black/10 text-black"
                          : "bg-[#FFD221]/15 text-[#FFD221]"
                      }`}
                    >
                      <ArrowRight size={17} strokeWidth={2.5} />
                    </motion.div>
                  </div>
                </motion.div>
              </TiltCard>
            );
          })}
        </div>

        {/* bottom note */}
        <motion.p
          className="mt-12 text-center text-xs font-semibold text-white/30"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          Free to explore · No account required to browse
        </motion.p>
      </div>
    </section>
  );
};

export default FinalCTA;
